import React, { useEffect } from 'react';
import { Type } from '@spotify-internal/encore-creator-web';
import { black } from '@spotify-internal/encore-foundation';
import { extractColor } from '@spotify-internal/web-color-extractor';
import styled from 'styled-components';
import {
  AddCopy,
  AddsList,
  AddRow,
  AddRowCoverArt,
  AddsCardContainer,
  CoverArt,
  Header,
  HeaderContainer,
  Heading,
} from './styles';
import moment from 'moment';
import { HomeTracker } from '../../../lib/Tracking/HomeTracker';
import { PlaylistAdd } from '../../../lib/Api/homeCards';
import { FormattedMessage } from 'react-intl';

const OPEN_SPOTIFY = 'https://open.spotify.com';

type Props = {
  imageUrl: string;
  playlistAdds: PlaylistAdd[];
  networkId: string | null;
  tracker: HomeTracker;
};

export function PlaylistAddsCard({ tracker, imageUrl, playlistAdds, networkId }: Props) {
  const [color, setColor] = React.useState('');

  useEffect(() => {
    tracker.trackImpression(
      tracker.makeHomeCardImpressionEvent(playlistAdds[0].homeCardId, 'playlist_add', networkId),
    );
  }, []);

  useEffect(() => {
    async function extractAndSetColor() {
      const hex = await extractColor(imageUrl, {
        ensureContrast: false,
        removeYellows: false,
        priority: ['LightVibrant'],
      });
      setColor(hex);
    }
    extractAndSetColor();
  }, []);

  const logInteraction = ({
    podcastId,
    networkId,
    homeCardId,
    destinationUrl,
  }: {
    podcastId: string;
    networkId: string | null;
    homeCardId: string;
    destinationUrl: string;
  }) => {
    tracker.trackInteraction(
      tracker.makeHomeCardInteractionEvent(
        homeCardId,
        'playlist_add',
        networkId,
        podcastId,
        destinationUrl,
      ),
    );
  };

  return (
    <AddsCardContainer>
      <HeaderContainer>
        <Header bgColor={color}>
          <WhiteOverlay />
          <a
            href={`${OPEN_SPOTIFY}/playlist/${playlistAdds[0].playlistId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <CoverArt src={imageUrl} />
          </a>
        </Header>
      </HeaderContainer>
      <Heading>
        <Type as="h2" typeColor={black} variant={Type.heading3}>
          <FormattedMessage
            id="home.playlist-add-title"
            defaultMessage="You've been added to our {playlistName} playlist!"
            values={{ playlistName: playlistAdds[0].playlistName }}
          />
        </Type>
      </Heading>
      <AddsList>
        {playlistAdds.map(
          ({
            episodeCoverArtUrl,
            episodeId,
            episodeName,
            playlistId,
            playlistName,
            podcastId,
            podcastName,
            timeAdded,
            homeCardId,
          }) => {
            const episodeUrl = `/episode/${episodeId}`;
            const showUrl = `/podcast/${podcastId}`;
            const playlistUrl = `${OPEN_SPOTIFY}/playlist/${playlistId}`;
            return (
              <AddRow key={`${episodeId}-${playlistId}-Row`}>
                <a
                  href={episodeUrl}
                  onClick={() =>
                    logInteraction({
                      podcastId,
                      networkId,
                      homeCardId,
                      destinationUrl: episodeUrl,
                    })
                  }
                >
                  <AddRowCoverArt src={episodeCoverArtUrl} alt={episodeName} />
                </a>
                <AddCopy>
                  <Type as="p" variant={Type.body2}>
                    <FormattedMessage
                      id="home.playlist-add-title"
                      defaultMessage="<episodeNameLink>{episodeName}</episodeNameLink> from <podcastNameLink>{podcastName}</podcastNameLink>
                        was added to <playlistNameLink>{playlistName}</playlistNameLink> on <strong>{dateAdded}</strong>."
                      values={{
                        episodeName: episodeName,
                        episodeNameLink: (...chunks) => (
                          <a
                            href={episodeUrl}
                            style={{ textDecoration: 'none' }}
                            onClick={() =>
                              logInteraction({
                                podcastId,
                                networkId,
                                homeCardId,
                                destinationUrl: episodeUrl,
                              })
                            }
                          >
                            <Type as="span" weight={Type.bold}>
                              {chunks}
                            </Type>
                          </a>
                        ),
                        podcastName: podcastName,
                        podcastNameLink: (...chunks) => (
                          <a
                            href={showUrl}
                            style={{ textDecoration: 'none' }}
                            onClick={() =>
                              logInteraction({
                                podcastId,
                                networkId,
                                homeCardId,
                                destinationUrl: showUrl,
                              })
                            }
                          >
                            <Type as="span" weight={Type.bold}>
                              {chunks}
                            </Type>
                          </a>
                        ),
                        playlistName: playlistName,
                        playlistNameLink: (...chunks) => (
                          <a
                            href={playlistUrl}
                            style={{ textDecoration: 'none' }}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              logInteraction({
                                podcastId,
                                networkId,
                                homeCardId,
                                destinationUrl: playlistUrl,
                              })
                            }
                          >
                            <Type as="span" weight={Type.bold}>
                              {chunks}
                            </Type>
                          </a>
                        ),
                        dateAdded: moment.unix(timeAdded / 1000).format('MMM[.] D[,] YYYY'),
                        strong: (...chunks) => <strong>{chunks}</strong>,
                      }}
                    />
                  </Type>
                </AddCopy>
              </AddRow>
            );
          },
        )}
      </AddsList>
    </AddsCardContainer>
  );
}

const WhiteOverlay = styled.div`
  background: rgba(255, 255, 255, 0.3);
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
`;
